import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { daysheetAPI, settingsAPI } from '../services/api';
import { getLocation, calculateDistance } from '../utils/locationUtils';
import { toast } from 'sonner';
import './PunchInOutWidget.css';

const Icon = ({ name, size = 20 }) => {
    const icons = {
        circle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /></svg>,
        checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
        clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    };
    return icons[name] || null;
};

const PunchInOutWidget = () => {
    const navigate = useNavigate();
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [workLocation, setWorkLocation] = useState('');
    const [activeTimer, setActiveTimer] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [daysheetSubmitted, setDaysheetSubmitted] = useState(false);
    const [workNotes, setWorkNotes] = useState('');

    const user = JSON.parse(localStorage.getItem('user'));
    // Make daysheet mandatory for everyone as per enterprise standards
    const requiresDaysheet = true;

    useEffect(() => {
        fetchTodayAttendance();
        checkActiveTimer();
        if (requiresDaysheet) {
            checkDaysheetStatus();
        }
    }, []);

    useEffect(() => {
        let interval;
        if (attendance?.punchInTime && !attendance?.punchOutTime) {
            interval = setInterval(() => {
                const start = new Date(attendance.punchInTime);
                const now = new Date();
                const diff = Math.floor((now - start) / 1000);
                setElapsedTime(diff);
            }, 1000);
        } else {
            setElapsedTime(0);
        }
        return () => clearInterval(interval);
    }, [attendance]);

    const fetchTodayAttendance = async () => {
        try {
            const res = await API.get('/attendance/my-today');
            setAttendance(res.data.data);
        } catch (error) {
            console.error('Error fetching attendance:', error);
        }
    };

    const checkActiveTimer = async () => {
        try {
            const res = await API.get('/task-time/active');
            setActiveTimer(res.data.data);
        } catch (error) {
            console.error('Error checking timer:', error);
        }
    };

    const checkDaysheetStatus = async () => {
        try {
            const res = await daysheetAPI.checkStatus();
            setDaysheetSubmitted(res.data.data.submitted);
        } catch (error) {
            console.error('Error checking daysheet:', error);
        }
    };

    const handlePunchInClick = () => {
        setShowLocationModal(true);
    };

    const handlePunchIn = async () => {
        if (!workLocation) {
            toast.error('Please select a work location');
            return;
        }

        setLoading(true);
        try {
            // Get GPS location with IP fallback
            const coords = await getLocation();

            if (!coords) {
                toast.error('Could not determine your location. Please enable location access or check your internet.');
                setLoading(false);
                return;
            }

            // We still capture coordinates automatically, and we can keep the detection 
            // logic for verification/logs, but we respect the user's manual choice.
            let detectedWorkLocation = 'WFH';
            try {
                const orgRes = await settingsAPI.getOrganization();
                const settings = orgRes.data.data.attendanceSettings;

                if (settings?.officeCoordinates?.latitude && settings?.officeCoordinates?.longitude) {
                    const distance = calculateDistance(
                        coords.latitude,
                        coords.longitude,
                        settings.officeCoordinates.latitude,
                        settings.officeCoordinates.longitude
                    );

                    if (distance <= (settings.officeRadius || 200)) {
                        detectedWorkLocation = 'Office';
                    }
                }
            } catch (e) {
                console.warn('Could not fetch organization settings');
            }

            const res = await API.post('/attendance/punch-in', {
                workLocation: workLocation, // Use the manually selected location
                location: coords,
                autoDetectedLocation: detectedWorkLocation // Optional: send what we detected for audit
            });

            setAttendance(res.data.data);
            setShowLocationModal(false);
            setWorkLocation(''); // Reset for next time
            toast.success(`Punched in successfully as ${workLocation}!`);
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Failed to punch in');
        } finally {
            setLoading(false);
        }
    };

    const handlePunchOut = async () => {
        // Redirect to dedicated daysheet page for mandatory reporting
        if (requiresDaysheet && !daysheetSubmitted) {
            navigate('/daysheet/today');
            return;
        }

        // If for some reason already submitted but not punched out, execute punch out directly
        executePunchOut();
    };

    const executePunchOut = async (finalNotes) => {
        setLoading(true);
        try {
            const coords = await getLocation();
            const res = await API.post('/attendance/punch-out', {
                workNotes: finalNotes || workNotes,
                location: coords
            });

            setAttendance(res.data.data);
            const totalHours = calculateTotalHours(res.data.data);
            toast.success(`Punched out successfully! Today's work: ${totalHours}`);
            setWorkNotes('');
        } catch (error) {
            if (error.response?.data?.requiresDaysheet) {
                setShowDaysheetModal(true);
            } else {
                toast.error(error.response?.data?.message || 'Failed to punch out');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDaysheetSubmitSuccess = (daysheetData) => {
        setDaysheetSubmitted(true);
        setShowDaysheetModal(false);
        // Automatically execute punch out with the summary as notes
        executePunchOut(daysheetData.workSummary);
    };

    const isPunchedIn = attendance && attendance.punchInTime && !attendance.punchOutTime;
    const isPunchedOut = !attendance || !attendance.punchInTime || attendance.punchOutTime;

    const formatTime = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const formatElapsedTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const calculateTotalHours = (attendanceData) => {
        if (!attendanceData?.punchInTime || !attendanceData?.punchOutTime) return '0h 0m';
        const start = new Date(attendanceData.punchInTime);
        const end = new Date(attendanceData.punchOutTime);
        const diff = Math.floor((end - start) / 1000);
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    const canPunchIn = !attendance || !attendance.punchInTime;
    const canPunchOut = attendance?.punchInTime && !attendance?.punchOutTime;

    return (
        <>
            <div className="punch-widget">
                <div className="punch-left">
                    {attendance && attendance.punchInTime && !attendance.punchOutTime ? (
                        <>
                            <div className="status-indicator in">
                                <span className="pulse-dot"></span>
                                <span className="status-text">Active Now</span>
                            </div>
                            <div className="punch-times">
                                <Icon name="clock" size={16} />
                                <span className="time-value">Punched in at {formatTime(attendance.punchInTime)}</span>
                            </div>
                            <div className="working-timer">
                                <span className="timer-label">Session:</span>
                                <span className="timer-value">{formatElapsedTime(elapsedTime)}</span>
                            </div>
                        </>
                    ) : attendance?.punchOutTime ? (
                        <>
                            <div className="status-indicator out">
                                <Icon name="checkCircle" size={18} />
                                <span className="status-text">Work Completed</span>
                            </div>
                            <div className="punch-summary">
                                <span className="summary-item">In: <strong>{formatTime(attendance.punchInTime)}</strong></span>
                                <span className="summary-divider"></span>
                                <span className="summary-item">Out: <strong>{formatTime(attendance.punchOutTime)}</strong></span>
                                <span className="summary-divider"></span>
                                <span className="summary-item">Total: <strong>{calculateTotalHours(attendance)}</strong></span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="status-indicator offline">
                                <div className="offline-dot"></div>
                                <span className="status-text">Not Punched In</span>
                            </div>
                            <p className="welcome-msg">Ready to start your workday?</p>
                        </>
                    )}
                </div>

                <div className="punch-actions">
                    {canPunchIn && (
                        <button
                            className="btn-punch btn-punch-in"
                            onClick={handlePunchInClick}
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Punch IN'}
                        </button>
                    )}
                    {canPunchOut && (
                        <button
                            className="btn-punch btn-punch-out"
                            onClick={handlePunchOut}
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Punch OUT'}
                        </button>
                    )}
                </div>
            </div>

            {showLocationModal && (
                <div className="modal-overlay" onClick={() => setShowLocationModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Where are you working from today?</h3>
                        <div className="location-options">
                            <label className={`location-option ${workLocation === 'Office' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="workLocation"
                                    value="Office"
                                    checked={workLocation === 'Office'}
                                    onChange={(e) => setWorkLocation(e.target.value)}
                                />
                                <div className="option-content">
                                    <span className="option-text">Office</span>
                                </div>
                            </label>
                            <label className={`location-option ${workLocation === 'WFH' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="workLocation"
                                    value="WFH"
                                    checked={workLocation === 'WFH'}
                                    onChange={(e) => setWorkLocation(e.target.value)}
                                />
                                <div className="option-content">
                                    <span className="option-text">Work From Home</span>
                                </div>
                            </label>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowLocationModal(false);
                                    setWorkLocation('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                onClick={handlePunchIn}
                                disabled={!workLocation || loading}
                            >
                                {loading ? 'Processing...' : 'Confirm Punch IN'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Removed redundant showPunchOutModal confirmation form */}
        </>
    );
};

export default PunchInOutWidget;
